import { useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import type { BoardColumn, WorkItemCard } from '../lib/types';

/**
 * The tester's two answers: it works, or it does not.
 *
 * WHY A PAIR OF BUTTONS AND NOT THE STATUS DROPDOWN. Testing is a decision, not a filing
 * exercise, and it is the same two moves every time — through to Done, or back with a reason.
 * A tester hunting for the right column in a list of seven is being asked to do the workflow's
 * bookkeeping instead of the job.
 *
 * "ISSUES FOUND" WRITES THE COMMENT AND BLOCKS IT IN ONE PRESS. Two steps by hand is two steps
 * to forget the second of, and a task blocked with no explanation is the thing whoever picks it
 * up next has to come and ask about. The note is posted as a comment first, and then the same
 * words become the reason the card is blocked.
 *
 * WHOEVER IS ON THE TASK IS TOLD BY THE MOVE ITSELF — bell and email, carrying the note. That
 * used to depend on the tester typing an @mention and picking the right person, which is a thing
 * to forget in exactly the moment it matters most. The comment still carries mentions, for
 * anybody ELSE who needs to know.
 *
 * Shown to whoever holds `work.move`: our own people and testers. A customer without the
 * tester role on this company never sees it.
 */
export function TestPanel({
  item,
  columns,
  onChanged,
}: {
  item: WorkItemCard;
  columns: BoardColumn[];
  onChanged: () => void | Promise<void>;
}) {
  const ask = useAsk();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const done = columns.find((column) => column.category === 'done');
  const blocked = columns.find((column) => column.category === 'blocked');

  /* Nothing to offer on a project whose workflow has no such column. */
  if (!done && !blocked) return null;

  /**
   * IT WORKS — through to Done, and it asks nothing.
   *
   * It briefly prompted for what fixed it, because the API refused without one. The demand has
   * gone and so has the prompt: a tester pressing this is confirming behaviour, and is very
   * often not whoever fixed it. What was fixed is recorded by whoever fixed it, in the
   * resolution box on the task.
   */
  async function passed() {
    if (!done || busy) return;

    setBusy(true);
    setError(null);
    try {
      await api.put(`/work-items/${item.id}/move`, { statusId: done.id, position: 0 });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not move it to done');
    } finally {
      setBusy(false);
    }
  }

  async function failed() {
    if (!blocked) return;

    const note = await ask.prompt({
      title: `What went wrong with ${item.key}?`,
      body:
        'Whoever is on this task is told, by email and in the Hub. This is posted as a comment too, so @ somebody else if they also need to know.',
      label: 'What failed',
      placeholder: 'The export still drops the last row on a month with 31 days',
      confirmLabel: 'Send it back',
      minLength: 3,
      multiline: true,
    });
    if (note === null || !note.trim() || busy) return;

    setBusy(true);
    setError(null);
    try {
      /* The comment first: it carries the @mention, and it survives even if the move fails. */
      await api.post(`/work-items/${item.id}/comments`, { body: note.trim() });
      await api.put(`/work-items/${item.id}/move`, {
        statusId: blocked.id,
        position: 0,
        reason: note.trim(),
      });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send it back');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card mt-2">
      <div className="card-head">
        <h3>Testing</h3>
      </div>
      <div className="card-pad">
        {error && <div className="callout mb-2">{error}</div>}

        <div className="row wrap" style={{ gap: 8 }}>
          {done && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void passed()}>
              Passed — move to {done.label}
            </button>
          )}
          {blocked && (
            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => void failed()}>
              Issues found — send it back
            </button>
          )}
        </div>

        <p className="dim" style={{ fontSize: 12, margin: '10px 0 0' }}>
          Sending it back blocks the card and posts what you write as a comment. Whoever picks it up
          moves it on again once it is fixed.
        </p>
      </div>
    </div>
  );
}
